<script setup lang="ts">
import type { EChartsOption } from 'echarts'
import { LazyVChart } from '~/components/ui/chart'
import type { IBankingKpi } from './types'
import { useBankingNumber } from './useBankingNumber'

const props = defineProps<{
  kpis: IBankingKpi[]
  asOf: string | null
}>()

const { t } = useI18n()
const { formatSom, formatPercent, formatCount, formatChange, formatDate } = useBankingNumber()

const formatValue = (kpi: IBankingKpi): string => {
  if (kpi.unit === 'percent') return formatPercent(kpi.value)
  if (kpi.unit === 'som') return formatSom(kpi.value)

  return formatCount(kpi.value)
}

// Growth of a volume is good news; a rate going up is neither good nor bad, so rates stay neutral.
const changeClass = (kpi: IBankingKpi): string => {
  if (!kpi.change || kpi.unit === 'percent' || kpi.change.value === 0) return 'text-gray-500'

  return kpi.change.value > 0 ? 'text-green-600' : 'text-red-600'
}

const hasSparkline = (kpi: IBankingKpi): boolean => kpi.trend.length > 1

const sparklineOption = (kpi: IBankingKpi): EChartsOption => ({
  grid: { left: 0, right: 0, top: 2, bottom: 2 },
  xAxis: { type: 'category', show: false, data: kpi.trend.map((point) => point.period) },
  yAxis: { type: 'value', show: false, scale: true },
  tooltip: { show: false },
  animation: false,
  series: [
    {
      type: 'line',
      data: kpi.trend.map((point) => point.value),
      showSymbol: false,
      smooth: true,
      silent: true,
      lineStyle: { width: 2, color: '#2563eb' },
      areaStyle: { color: 'rgba(37, 99, 235, 0.08)' }
    }
  ]
})

const isOtherDate = (kpi: IBankingKpi): boolean => !!props.asOf && kpi.as_of !== props.asOf

const MONTHS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december'
] as const

/** Net profit is summed from the start of the year: "январь–август" for a value as of August. */
const ytdPeriod = (kpi: IBankingKpi): string | null => {
  if (kpi.key !== 'net_profit') return null

  const month = MONTHS[Number(kpi.as_of.slice(5, 7)) - 1]

  if (!month) return null
  if (month === 'january') return t('banking_statistics.months.january')

  return t('banking_statistics.kpi.ytd_period', {
    month: t(`banking_statistics.months.${month}`)
  })
}
</script>

<template>
  <div v-if="kpis.length" id="summary" class="scroll-mt-24 grid grid-cols-2 gap-3 lg:grid-cols-3 lg:gap-4">
    <div
      v-for="kpi in kpis"
      :key="kpi.key"
      data-testid="banking-kpi"
      :data-key="kpi.key"
      class="flex min-w-0 flex-col gap-1 rounded-lg border border-gray-200 bg-white p-3 sm:p-4"
    >
      <div class="text-xs text-gray-500 sm:text-sm">
        {{ t(`banking_statistics.kpi.${kpi.key}`) }}
      </div>
      <div class="text-lg font-semibold leading-tight text-gray-900 sm:text-2xl">
        {{ formatValue(kpi) }}
      </div>
      <div v-if="ytdPeriod(kpi)" class="text-xs text-gray-500 sm:text-sm">
        {{ ytdPeriod(kpi) }}
      </div>
      <div v-if="kpi.change" class="text-xs font-medium sm:text-sm" :class="changeClass(kpi)">
        {{ formatChange(kpi.change) }}
      </div>
      <LazyVChart
        v-if="hasSparkline(kpi)"
        :option="sparklineOption(kpi)"
        autoresize
        class="mt-1 h-10 w-full"
      />
      <div class="mt-auto text-[11px] text-gray-400">
        <span v-if="kpi.is_preliminary">{{ t('banking_statistics.preliminary') }}</span>
        <span v-if="kpi.is_preliminary && isOtherDate(kpi)"> · </span>
        <span v-if="isOtherDate(kpi)">
          {{ t('banking_statistics.as_of_short', { date: formatDate(kpi.as_of) }) }}
        </span>
      </div>
    </div>
  </div>
  <div
    v-else
    class="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500"
  >
    {{ t('banking_statistics.empty') }}
  </div>
</template>
